import React from 'react';
import env from '../../environment.js';

// Piezas compartidas por toda página de impresión "estilo croquis" de la app
// (parcelas-profile-print.js, padron-mapas-print.js): mismo layout A4 vertical,
// mismas cards de datos y misma tabla de símbología, para que el resultado
// impreso se vea igual sin importar de qué nivel/entidad se trate.

// Ancho del área imprimible de una hoja A4 vertical (210mm − 2 × 10mm de margen
// en @page). El mapa impreso es una copia del de pantalla y Google Maps solo
// carga los mosaicos del tamaño visible: si en pantalla es más angosto que la
// hoja, la copia muestra franjas grises; si es más ancho, se recorta limpio
// desde el centro. Ver prepararMapaParaImprimir.
export const PRINT_SHEET_WIDTH_MM = 190;
export const PRINT_SHEET_WIDTH = `${PRINT_SHEET_WIDTH_MM}mm`;

// Verifica si los mosaicos (<img>) de Google Maps ya descargados cubren todo el
// área visible de `raiz`. Devuelve true/false, o null si no hay mosaicos <img>
// (cobertura desconocida, p. ej. render vectorial en canvas).
// Se muestrea una cuadrícula de puntos (cada 48 px, con 2 px de margen en los
// bordes e incluyendo siempre el último punto de cada eje) y cada punto debe
// caer dentro de al menos un mosaico. Solo se consideran imágenes completas y de
// al menos 128 px de ancho, para excluir los íconos de marcadores.
const PASO_MUESTREO_PX = 48;
const MARGEN_MUESTREO_PX = 2;

const mosaicosCubrenMapa = raiz => {
  const r = raiz.getBoundingClientRect();
  const mosaicos = Array.from(raiz.querySelectorAll('img'))
    .filter(img => img.complete && img.naturalWidth > 0 && img.getBoundingClientRect().width >= 128)
    .map(img => img.getBoundingClientRect());

  if (mosaicos.length === 0) return null;

  const muestras = (desde, hasta) => {
    const valores = [];
    for (let v = desde; v < hasta; v += PASO_MUESTREO_PX) valores.push(v);
    valores.push(hasta);
    return valores;
  };

  const xs = muestras(r.left + MARGEN_MUESTREO_PX, r.right - MARGEN_MUESTREO_PX);
  const ys = muestras(r.top + MARGEN_MUESTREO_PX, r.bottom - MARGEN_MUESTREO_PX);

  return ys.every(y => xs.every(x => mosaicos.some(t => x >= t.left && x <= t.right && y >= t.top && y <= t.bottom)));
};

// Antes de imprimir: si el mapa en pantalla es más angosto que el área
// imprimible, lo expande SIN que el usuario lo note y espera a que Google Maps
// cargue los mosaicos de ese tamaño. Devuelve (vía Promise) una función que
// restaura el estado original.
//
// - Mapa en pantalla >= hoja: no hace nada (el contenedor del mapa impreso pasa
//   a 190mm y los panes de Google, anclados al centro, dan un recorte limpio).
// - Mapa en pantalla < hoja: la raíz de google-map-react (hijo directo del
//   wrapper que contiene .gm-style) se ensancha a 190mm con margen izquierdo
//   negativo, de modo que crece por igual a ambos lados y el wrapper
//   (overflow: hidden) recorta lo que sobra. Centro, zoom y escala en píxeles
//   no cambian, así que lo que se ve dentro del wrapper es idéntico. La clase
//   `print-map-expandido` (ver pageStyle) hace que en la hoja ocupe justo el
//   ancho del wrapper.
export const prepararMapaParaImprimir = (wrapperEl, map, maps) =>
  new Promise(resolve => {
    const sinCambios = () => resolve(() => {});

    if (!wrapperEl || !map || !maps) {
      sinCambios();
      return;
    }

    const anchoPantalla = wrapperEl.getBoundingClientRect().width;
    const anchoHojaPx = (PRINT_SHEET_WIDTH_MM * 96) / 25.4;
    if (anchoPantalla >= anchoHojaPx - 1) {
      sinCambios();
      return;
    }

    // Raíz de google-map-react: hijo directo del wrapper que contiene .gm-style.
    let raiz = wrapperEl.querySelector('.gm-style');
    while (raiz && raiz.parentElement !== wrapperEl) raiz = raiz.parentElement;
    if (!raiz) {
      sinCambios();
      return;
    }

    const prevWidth = raiz.style.width;
    const prevMarginLeft = raiz.style.marginLeft;
    const prevOverflow = wrapperEl.style.overflow;
    const center = map.getCenter();
    let resuelto = false;
    const timers = [];
    const intervalos = [];

    const restaurar = () => {
      raiz.style.width = prevWidth;
      raiz.style.marginLeft = prevMarginLeft;
      wrapperEl.style.overflow = prevOverflow;
      raiz.classList.remove('print-map-expandido');
      maps.event.trigger(map, 'resize');
      map.setCenter(center);
    };

    const terminar = () => {
      if (resuelto) return;
      resuelto = true;
      timers.forEach(clearTimeout);
      intervalos.forEach(clearInterval);
      resolve(restaurar);
    };

    // Tope global: red de seguridad por si los mosaicos nunca terminan de
    // cubrir el mapa. Nunca bloquear la impresión más de 3 s.
    timers.push(setTimeout(terminar, 3000));

    wrapperEl.style.overflow = 'hidden';
    raiz.style.width = PRINT_SHEET_WIDTH;
    // Margen negativo: crece por igual a ambos lados, clipeado por el wrapper.
    raiz.style.marginLeft = `calc((100% - ${PRINT_SHEET_WIDTH}) / 2)`;
    raiz.classList.add('print-map-expandido');

    maps.event.trigger(map, 'resize');
    map.setCenter(center);

    // Esperar por eventos ("bounds_changed" → "tilesloaded", o un respaldo por
    // tiempo) no es confiable: en una primera carga sin caché la promesa se
    // resolvía antes de que terminaran de descargarse los mosaicos nuevos y la
    // impresión salía con zonas grises. En lugar de confiar en el orden de los
    // eventos, se revisa directamente el resultado: se sondea el DOM hasta que
    // los <img> de mosaicos ya cargados cubren por completo el área visible.
    //
    // Los primeros 300 ms se ignoran: Google Maps tarda en detectar el nuevo
    // tamaño y en crear los elementos de los mosaicos nuevos; antes de eso solo
    // existen los mosaicos del mapa angosto, que ya "cubren" el área anterior.
    const inicio = Date.now();
    let cierreProgramado = false;
    const sondear = () => {
      const transcurrido = Date.now() - inicio;
      if (transcurrido < 300) return;

      const cubierto = mosaicosCubrenMapa(raiz);
      if (cubierto === true) {
        // Cobertura completa: margen corto para que se pinten.
        if (cierreProgramado) return;
        cierreProgramado = true;
        timers.push(setTimeout(terminar, 100));
      } else if (cubierto === null && transcurrido >= 1500) {
        // Sin mosaicos <img> (p. ej. render vectorial/canvas): no hay forma de
        // verificar la cobertura, se da un margen fijo y se continúa.
        terminar();
      }
    };
    intervalos.push(setInterval(sondear, 100));
  });

export const pageStyle = `
  @media print {
    /* Chrome/Edge 131+ omiten sus encabezados y pies de página (fecha, título,
       URL) en cada borde donde haya un margin box definido, aunque la casilla
       esté marcada: @top-right vacío quita el encabezado y @bottom-right
       reemplaza el pie por nuestro número de página. En Safari/Firefox no
       aplica (depende de la casilla). Los fondos ya no dependen de la casilla
       "Gráficos de fondo" gracias a print-color-adjust: exact en html/body
       (se hereda). Bootstrap 4 trae "@page { size: a3 }" en su bloque de
       impresión y se copia después de esta hoja; !important asegura A4. */
    @page {
      size: A4 portrait !important;
      margin: 10mm;

      @top-right {
        content: "";
      }

      @bottom-right {
        content: "Página " counter(page) " de " counter(pages);
        font-family: Arial, Helvetica, sans-serif;
        font-size: 8px;
        color: #8a8a8a;
      }
    }

    /* "html body" (mayor especificidad) para vencer el
       "body { min-width: 992px !important }" del bloque de impresión de
       Bootstrap 4, que ensancharía la hoja más allá de A4. */
    html, html body {
      min-width: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      background: #fff !important;
    }

    .no-print { display: none !important; }

    .print-root {
      width: 100% !important;
      margin: 0 !important;
    }

    .print-page {
      width: 100% !important;
    }

    .print-page-first {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    .print-avoid-break {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    /* Fuerza que la tabla de coordenadas arranque en una hoja nueva
       (se aplica solo cuando hay muchos puntos, ver UMBRAL_PUNTOS_NUEVA_HOJA) */
    .print-page-break-before {
      break-before: page;
      page-break-before: always;
    }

    /* En el iframe de impresión los breakpoints col-md-* no aplican, así que
       las cards se apilarían. Forzamos las 3 en una sola fila. */
    .print-info-cards {
      display: flex !important;
      flex-wrap: nowrap !important;
    }

    .print-info-cards > div {
      flex: 0 0 33.33% !important;
      max-width: 33.33% !important;
    }

    /* Solo tablas que optan explícitamente por bordes visibles (.table-bordered)
       reciben este trazo forzado — las tablas del rediseño (zebra sin bordes,
       ver padron-mapas-print.js) usan className="table" a secas y no deben
       verse afectadas por esta regla legada. */
    .table-bordered tr {
      border: 1px solid #000 !important;
    }

    td, th {
      font-size: 9px !important;
      padding: 3px 6px !important;
    }

    /* Encabezado/zebra de las listas de padron-mapas-print.js: armadas con
       flexbox (ver FlexTableRow/FlexTableCell) en vez de <table>/<tr>/<td>
       porque el background-color de elementos de tabla no se imprime en el
       motor de impresión real, aunque tengan print-color-adjust:exact — un
       <div> normal sí. Estas dos clases reproducen, sobre divs, el mismo
       ajuste de tamaño/borde que "td, th" y ".table-bordered tr" le dan a las
       tablas reales de arriba. */
    .print-cell {
      font-size: 9px !important;
      padding: 3px 6px !important;
    }

    .print-cell-bordered {
      border: 1px solid #000 !important;
    }

    /* contain: strict aísla el mapa: sus mosaicos se extienden más allá del
       recuadro (están recortados con overflow, pero Chrome igual los toma en
       cuenta al paginar) y, sin esto, Chrome encoge TODA la página para que
       "quepan" cuando el mapa en pantalla es más ancho que la hoja. */
    .print-map-wrapper {
      width: 100% !important;
      height: 540px !important;
      page-break-inside: avoid;
      contain: strict;
    }

    .print-map-wrapper > div > div {
      border-radius: 0 !important;
    }

    /* El mapa se expande (centrado y recortado por el wrapper) solo mientras se
       copia a la impresión; en la hoja ocupa exactamente el ancho del wrapper. */
    .print-map-expandido {
      width: 100% !important;
      margin-left: 0 !important;
    }

    /* Botones de pantalla completa de Google Maps y de recentrar el mapa: son
       controles de pantalla, no tienen sentido en la hoja impresa. */
    .gm-fullscreen-control,
    .map-recenter-button {
      display: none !important;
    }

    .print-col-box {
      border: 1px solid #888 !important;
      background: #f9f9f9 !important;
    }

    .simbologia-section {
      page-break-inside: avoid;
      margin-top: 6px !important;
    }

    .print-fixed-header {
      display: flex !important;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 6mm;
      padding-bottom: 3mm;
      border-bottom: 1px solid ${env.colors.neutral_100};
    }

    .print-fixed-footer {
      display: block !important;
      text-align: center;
      margin-top: 6mm;
      padding-top: 3mm;
      border-top: 1px solid ${env.colors.neutral_100};
    }
  }
`;

// Line style swatch — print-friendly CSS border
export const PrintLineSwatch = ({ style }) => {
  const { color, dashType, weight } = style;
  const borderStyle = dashType === 'solid' ? 'solid' : dashType === 'dotted' ? 'dotted' : 'dashed';
  return (
    <span
      style={{
        display: 'inline-block',
        width: 24,
        height: 0,
        borderTop: `${weight || 2}px ${borderStyle} ${color}`,
        marginRight: 5,
        verticalAlign: 'middle',
        flexShrink: 0,
      }}
    />
  );
};

export const PrintAreaSwatch = ({ fill }) => (
  <span
    style={{
      display: 'inline-block',
      width: 14,
      height: 14,
      borderRadius: 2,
      backgroundColor: fill.fill,
      border: `1.5px solid ${fill.stroke}`,
      marginRight: 5,
      verticalAlign: 'middle',
      flexShrink: 0,
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
    }}
  />
);

// Campo clave-valor con el idioma tipográfico de la app (Lato, etiqueta
// atenuada + valor destacado), igual que CardProductor / ficha de coop.
export const InfoField = ({ label, value, mono = false }) => {
  const tieneValor = value === 0 || (value !== null && value !== undefined && value !== '');
  return (
    <div className="mb-1">
      <div className="font-lato-normal-400" style={{ color: env.colors.neutral_700, fontSize: 11, lineHeight: 1.3 }}>
        {label}
      </div>
      <div
        className="font-lato-normal-600"
        style={{
          color: env.colors.primary_900,
          fontSize: 14,
          lineHeight: 1.35,
          fontVariantNumeric: mono ? 'tabular-nums' : 'normal',
          wordBreak: 'break-word',
        }}>
        {tieneValor ? value : '—'}
      </div>
    </div>
  );
};

// Agrupa campos relacionados en una card con header de color (banda verde
// clara + cuerpo blanco), congruente con la paleta de la app.
export const InfoCard = ({ title, children, col = 'col-12 col-md-4' }) => (
  <div className={`${col} mb-3`}>
    <div
      className="print-avoid-break"
      style={{
        backgroundColor: '#fff',
        border: `1px solid ${env.colors.neutral_100}`,
        borderRadius: 5,
        overflow: 'hidden',
        height: '100%',
      }}>
      <div
        className="font-lato-normal-600"
        style={{
          backgroundColor: 'rgba(249,250,244,0.95)',
          color: env.colors.primary_700,
          padding: '8px 16px',
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: '0.02em',
          borderBottom: `1px solid ${env.colors.neutral_100}`,
        }}>
        {title}
      </div>
      <div style={{ padding: '12px 16px' }}>{children}</div>
    </div>
  </div>
);

// Tarjeta "Simbología" en su versión imprimible: misma información que
// SimbologiaLegend (puntos/textos, líneas, áreas) pero como tabla de 3
// columnas con estilos print-safe, sin botones interactivos (Mostrar/Ocultar,
// Editar) que no tienen sentido en una hoja impresa.
// `titleBg`/`titleColor`/`bodyBg` son opcionales — por defecto reproduce el
// estilo crema original (parcelas-profile-print.js): título en texto verde
// dentro de la misma caja crema del cuerpo. `titleOutside` (usado por
// padron-mapas-print.js) saca el título de la caja bordeada, como un
// encabezado de sección normal — sin afectar el resto de páginas que usan
// este mismo componente (quedan con su valor por defecto, `false`).
// `columnHeaderBg`/`columnHeaderColor` (usado por padron-mapas-print.js)
// reemplaza el rótulo de columna en texto plano por una barra de color
// a todo lo ancho de la caja (edge-to-edge), como el resto de encabezados
// de tabla del rediseño. Por defecto no se renderiza (`columnHeaderBg`
// vacío), dejando el estilo de texto plano original para las demás páginas.
export const PrintSimbologiaTable = ({
  legendData,
  panTo,
  coordsCenter,
  extraArea,
  extraPoint,
  title = 'Simbología',
  bodyBg = '#f9faf4',
  titleBg = 'transparent',
  titleColor = '#4A6700',
  borderColor = '#c8d1a0',
  titleOutside = false,
  columnHeaderBg,
  columnHeaderColor = '#fff',
}) => {
  const hasPointsOrTexts = legendData.points.length > 0 || legendData.texts.length > 0 || !!extraPoint;
  const hasLines = legendData.lines.length > 0;
  const hasPolygons = legendData.polygons.length > 0 || !!extraArea;
  const hasColumnHeaderBar = !!columnHeaderBg;
  // `titleOutside` distingue el uso del rediseño de padron-mapas-print.js del
  // estilo crema original (parcelas-profile-print.js, titleOutside=false) —
  // se reutiliza también para el tamaño de fuente del cuerpo de la tabla.
  const bodyFontSize = titleOutside ? 13 : 10;

  if (!hasPointsOrTexts && !hasLines && !hasPolygons) return null;

  const titleEl = (
    <div
      style={{
        fontWeight: 700,
        fontSize: titleOutside ? 18 : 11,
        color: titleOutside ? '#333333' : titleColor,
        backgroundColor: titleOutside ? 'transparent' : titleBg,
        textTransform: titleOutside ? 'none' : 'uppercase',
        letterSpacing: titleOutside ? 'normal' : 0.8,
        padding: titleOutside ? 0 : '6px 12px',
        marginBottom: titleOutside ? 8 : 6,
      }}>
      {title}
    </div>
  );

  return (
    <>
      {titleOutside && titleEl}
      <div
        className="simbologia-section"
        style={{
          border: `1px solid ${borderColor}`,
          borderRadius: 6,
          overflow: 'hidden',
          backgroundColor: bodyBg,
          marginBottom: 6,
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact',
        }}>
        {!titleOutside && titleEl}
        {hasColumnHeaderBar && (
          <div style={{ display: 'flex', backgroundColor: columnHeaderBg, color: columnHeaderColor }}>
            <div style={{ width: '33%', padding: '6px 12px', fontWeight: 700, fontSize: bodyFontSize, textTransform: 'uppercase' }}>
              Puntos / Textos
            </div>
            <div style={{ width: '33%', padding: '6px 12px', fontWeight: 700, fontSize: bodyFontSize, textTransform: 'uppercase' }}>
              Líneas
            </div>
            <div style={{ width: '33%', padding: '6px 12px', fontWeight: 700, fontSize: bodyFontSize, textTransform: 'uppercase' }}>
              Áreas
            </div>
          </div>
        )}
        <table
          style={{
            width: 'calc(100% - 24px)',
            margin: hasColumnHeaderBar ? '8px 12px' : '0 12px 8px',
            borderCollapse: 'collapse',
            fontSize: bodyFontSize,
          }}>
          <tbody>
            <tr style={{ verticalAlign: 'top' }}>
              {/* PUNTOS / TEXTOS column */}
              <td style={{ width: '33%', paddingRight: 16, borderRight: '1px solid #ddd' }}>
                {hasPointsOrTexts && (
                  <>
                    {!hasColumnHeaderBar && (
                      <div style={{ fontWeight: 600, textTransform: 'uppercase', marginBottom: 4, fontSize: bodyFontSize }}>
                        Puntos / Textos
                      </div>
                    )}
                    {extraPoint && (
                      <div
                        onClick={extraPoint.onClick}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: 3,
                          cursor: extraPoint.onClick ? 'pointer' : 'default',
                        }}>
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: extraPoint.color || '#000',
                            marginRight: 5,
                            flexShrink: 0,
                            display: 'inline-block',
                            WebkitPrintColorAdjust: 'exact',
                            printColorAdjust: 'exact',
                          }}
                        />
                        <span style={{ fontSize: bodyFontSize }}>{extraPoint.label}</span>
                      </div>
                    )}
                    {legendData.points.map((item, i) => (
                      <div
                        key={i}
                        onClick={() => panTo({ lat: item.lat, lng: item.lng })}
                        style={{ display: 'flex', alignItems: 'center', marginBottom: 3, cursor: 'pointer' }}>
                        <img
                          src={item.config.iconUrl}
                          alt={item.config.label}
                          style={{
                            width: 16,
                            height: 16,
                            marginRight: 5,
                            flexShrink: 0,
                            WebkitPrintColorAdjust: 'exact',
                            printColorAdjust: 'exact',
                          }}
                        />
                        <span style={{ fontSize: bodyFontSize }}>{item.config.label}</span>
                      </div>
                    ))}
                    {legendData.texts.map((item, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: 3 }}>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 16,
                            height: 14,
                            borderRadius: 2,
                            backgroundColor: 'rgba(255,255,255,0.92)',
                            border: '1px solid #c8d1a0',
                            fontSize: 9,
                            fontWeight: 700,
                            color: '#4A6700',
                            marginRight: 5,
                            flexShrink: 0,
                            WebkitPrintColorAdjust: 'exact',
                            printColorAdjust: 'exact',
                          }}>
                          T
                        </span>
                        <span style={{ fontSize: bodyFontSize }}>{item.nombre || `Texto ${i + 1}`}</span>
                      </div>
                    ))}
                  </>
                )}
              </td>
              {/* LÍNEAS column */}
              <td style={{ width: '33%', paddingLeft: 16, paddingRight: 16, borderRight: '1px solid #ddd' }}>
                {hasLines && (
                  <>
                    {!hasColumnHeaderBar && (
                      <div style={{ fontWeight: 600, textTransform: 'uppercase', marginBottom: 4, fontSize: bodyFontSize }}>Líneas</div>
                    )}
                    {legendData.lines.map((item, i) => (
                      <div
                        key={i}
                        onClick={() => panTo(coordsCenter(item.coordinates))}
                        style={{ display: 'flex', alignItems: 'center', marginBottom: 3, cursor: 'pointer' }}>
                        <PrintLineSwatch style={item.style} />
                        <span style={{ fontSize: bodyFontSize }}>{item.nombre || `Línea ${i + 1}`}</span>
                      </div>
                    ))}
                  </>
                )}
              </td>
              {/* ÁREAS column */}
              <td style={{ width: '33%', paddingLeft: 16 }}>
                {hasPolygons && (
                  <>
                    {!hasColumnHeaderBar && (
                      <div style={{ fontWeight: 600, textTransform: 'uppercase', marginBottom: 4, fontSize: bodyFontSize }}>Áreas</div>
                    )}
                    {extraArea && (
                      <div
                        onClick={extraArea.onClick}
                        style={{ display: 'flex', alignItems: 'center', marginBottom: 3, cursor: 'pointer' }}>
                        <PrintAreaSwatch fill={{ fill: '#f2f1e8', stroke: '#91B833' }} />
                        <span style={{ fontSize: bodyFontSize }}>{extraArea.label}</span>
                      </div>
                    )}
                    {legendData.polygons.map((item, i) => (
                      <div
                        key={i}
                        onClick={() => panTo(coordsCenter(item.coordinates))}
                        style={{ display: 'flex', alignItems: 'center', marginBottom: 3, cursor: 'pointer' }}>
                        <PrintAreaSwatch fill={item.fill} />
                        <span style={{ fontSize: bodyFontSize }}>{item.nombre || `Área ${i + 1}`}</span>
                      </div>
                    ))}
                  </>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
};
